import { useState, useRef, useEffect } from 'react';
import { Button, Stack, TextField, ErrorMessage } from '@krakentech/coral';
import { IconAdd, IconMinus } from '@krakentech/icons';
import { useFormikContext } from 'formik';
import styled, { css } from 'styled-components';

import { piiDataAttributes } from '@/components/PII/constants';

type NumberStepperProps = {
  step?: number;
  maxCount?: number;
  minCount?: number;
  onIncrement?: (newCount: number) => void;
  onDecrement?: (newCount: number) => void;
  name: string;
  label: string;
};

const NumberStepper = ({
  step = 1,
  maxCount = Infinity,
  minCount = 0,
  onIncrement,
  onDecrement,
  name,
  label,
}: NumberStepperProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const { setFieldValue, values, errors } = useFormikContext<{
    [key: string]: number;
  }>();

  // `values[name]` will default the input to what a pre selected value is.
  // For example, when a value is saved in the context of a multistep journey.
  const [quantity, setStepperQuantity] = useState(values[name] || minCount);

  const setQuantity = (value: number) => {
    setStepperQuantity(value);
    setFieldValue(name, value);
  };

  const handleIncrement = () => {
    const newCount = Math.min(quantity + step, maxCount);
    setQuantity(newCount);
    onIncrement?.(newCount);
  };

  const handleDecrement = () => {
    const newCount = Math.max(quantity - step, minCount);
    setQuantity(newCount);
    onDecrement?.(newCount);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = parseInt(event.target.value);

    if (!isNaN(inputValue)) {
      setQuantity(inputValue);
    } else if (isNaN(inputValue) || inputValue < minCount) {
      setQuantity(minCount - 1);
    } else if (isNaN(inputValue) || inputValue > maxCount) {
      setQuantity(maxCount + 1);
    } else {
      setQuantity(minCount);
    }
  };

  // Overrides the  native number input validation popup which allows us to display our own
  useEffect(() => {
    const numberInput = inputRef.current;

    const handleInvalid = (event: Event) => {
      event.preventDefault();
    };

    if (numberInput) {
      numberInput.addEventListener('invalid', handleInvalid, true);
    }

    return () => {
      if (numberInput) {
        numberInput.removeEventListener('invalid', handleInvalid, true);
      }
    };
  }, []);

  const errorMessage = errors[name];

  return (
    <Stack direction="vertical">
      <Stack gap="sm">
        <Button
          onClick={handleDecrement}
          disabled={quantity <= minCount}
          size="large"
          aria-label="Decrement"
        >
          <IconMinus color="white" size={10} />
        </Button>
        <NumberStepperInputOverride>
          <TextField
            ref={inputRef}
            name={name}
            label={label}
            onChange={handleInputChange}
            type="number"
            min={minCount}
            max={maxCount}
            value={values[name].toString()}
            attributes={piiDataAttributes}
          />
        </NumberStepperInputOverride>
        <Button
          onClick={handleIncrement}
          disabled={quantity >= maxCount}
          size="large"
          aria-label="Increment"
        >
          <IconAdd color="white" size={10} />
        </Button>
      </Stack>
      {errorMessage && <ErrorMessage message={errorMessage} />}
    </Stack>
  );
};

// As this is an edge case use of an input it seems ok to override these base coral styles here.
// Perhaps once this component gets added to coral itself, we can create bespoke styles for it.
export const NumberStepperInputOverride = styled.div`
  ${({ theme }) => css`
    width: 100%;

    @media (min-width: ${theme.breakpoints.sm}px) {
      width: 12rem;
    }

    input {
      text-align: center;
    }

    fieldset {
      top: 0;
    }

    fieldset label,
    label + fieldset legend {
      display: none;
    }

    /* Hide arrows for Chrome, Safari, Edge, and Opera */
    input[type='number']::-webkit-inner-spin-button,
    input[type='number']::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Hide arrows for Firefox */
    input[type='number'] {
      -moz-appearance: textfield;
    }
  `}
`;

export default NumberStepper;
